export interface AppAlert {
  id: string;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error';
  dismissible?: boolean;
  action?: {
    label: string;
    run: () => void | Promise<unknown>;
    disabled?: boolean;
    navigate?: boolean;
  };
}
export interface StoredAlert extends AppAlert {
  key: string;
  userId: string;
  updatedAt: number;
}

/** Session-only presentation state. Commands remain owned by their existing controllers. */
export function createAlertStore() {
  const sources = new Map<string, StoredAlert[]>();
  const dismissed = new Set<string>();
  const listeners = new Set<() => void>();
  let account: string | undefined;
  let snapshot: StoredAlert[] = [];
  const signature = (entries: AppAlert[]) => JSON.stringify(entries);
  const publish = () => {
    snapshot = [...sources.values()]
      .flat()
      .filter((entry) => !dismissed.has(entry.key));
    listeners.forEach((listener) => listener());
  };
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    setAccount(userId?: string) {
      if (account === userId) return;
      account = userId;
      sources.clear();
      dismissed.clear();
      publish();
    },
    replace(userId: string, source: string, alerts: AppAlert[]) {
      if (account !== userId) return;
      const sourceKey = JSON.stringify([userId, source]);
      const previous = sources.get(sourceKey) || [];
      const next = alerts.map((alert) => {
        const key = JSON.stringify([userId, source, alert.id]);
        const old = previous.find((entry) => entry.key === key);
        return {
          ...alert,
          key,
          userId,
          updatedAt: old?.updatedAt || Date.now(),
        };
      });
      previous.forEach((entry) => {
        if (!next.some((candidate) => candidate.key === entry.key))
          dismissed.delete(entry.key);
      });
      // Refresh callbacks even when the visible content has not changed.
      sources.set(sourceKey, next);
      if (signature(previous) !== signature(next)) publish();
    },
    remove(userId: string, source: string) {
      const key = JSON.stringify([userId, source]);
      (sources.get(key) || []).forEach((entry) => dismissed.delete(entry.key));
      if (sources.delete(key)) publish();
    },
    notice(userId: string, alert: AppAlert) {
      if (account !== userId) return;
      const source = JSON.stringify([userId, `notice:${alert.id}`]);
      const key = JSON.stringify([userId, `notice:${alert.id}`, alert.id]);
      dismissed.delete(key);
      sources.set(source, [
        { ...alert, dismissible: true, key, userId, updatedAt: Date.now() },
      ]);
      publish();
    },
    dismiss(key: string) {
      if (!snapshot.find((entry) => entry.key === key)?.dismissible) return;
      dismissed.add(key);
      publish();
    },
    async run(key: string) {
      const entry = [...sources.values()]
        .flat()
        .find((candidate) => candidate.key === key);
      if (!entry || entry.userId !== account || entry.action?.disabled) return;
      return entry.action?.run();
    },
  };
}
